/* eslint-disable @typescript-eslint/no-unused-vars */
import type { Ref } from 'react';
import { forwardRef, useCallback, useMemo, useContext } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import PopupState, { bindToggle, bindPopper } from 'material-ui-popup-state';
import type { PopupState as PopupStateProps } from 'material-ui-popup-state/hooks';
import { PopperMenu, PopperMenuItem } from '@trinity/chat-components/components/Popper/index.js';
import { TranscribeFileContext, ViewFileTranscriptContext } from '@/containers/Workspace.File/Context';
import { useFileMenuItems, useFileActions, useShowFileMenu } from '@/containers/Workspace.File/hooks';
import { MoreHorizontalAnchorSmall } from '../presentation/Anchor';
import type { FileActionsMenuProps, FileActionsPopperProps } from './interfaces';

const FileActionsContextMenu = forwardRef(({ onClose }: FileActionsMenuProps, ref: Ref<HTMLDivElement>) => {
  const valid = useFileMenuItems();
  const actions = useFileActions();

  const transcribeFileContext = useContext(TranscribeFileContext);
  const viewTranscriptContext = useContext(ViewFileTranscriptContext);

  const handleClick = useCallback((fn: () => unknown) => () => {
    onClose();
    return fn();
  }, [onClose]);

  const remove = useMemo(() => {
    return {
      children: 'Remove',
      onClick: actions.delete,
    };
  }, [actions.delete]);

  const ownership = useMemo(() => {
    return {
      children: 'Change Owner',
      onClick: actions.changeOwner,
    };
  }, [actions.changeOwner]);

  const move = useMemo(() => {
    return {
      children: 'Move',
      onClick: actions.move,
    };
  }, [
    actions.move,
  ]);

  const download = useMemo(() => {
    return {
      children: 'Download',
      onClick: actions.download,
    };
  }, [
    actions.download,
  ]);

  const rename = useMemo(() => {
    return {
      children: 'Rename',
      onClick: actions.rename,
    };
  }, [actions.rename]);


  const manageAccess = useMemo(() => {
    return {
      children: 'Manage Access',
      onClick: actions.manageAccess,
    };
  }, [actions.manageAccess]);

  const transcribe = useMemo(() => {
    return {
      children: 'Transcribe',
      onClick: transcribeFileContext,
    };
  }, [transcribeFileContext]);

  const viewTranscript = useMemo(() => {
    return {
      children: 'View Transcript',
      onClick: viewTranscriptContext,
    };
  }, [viewTranscriptContext]);

  const viewFile = useMemo(() => {
    return {
      children: 'View File',
      onClick: actions.viewFile,
    };
  }, [actions.viewFile]);

  // const convertToTranscriptContext = useContext(ImportTranscriptContext);
  // const convertToTranscript = useMemo(() => {
  //   return {
  //     children: 'Import Transcript',
  //     onClick: convertToTranscriptContext,
  //   };
  // }, [convertToTranscriptContext]);

  const items = useMemo(() => {
    return [
      viewFile,
      // valid.canChangeOwner && ownership,
      valid.canDownload && download,
      valid.canMove && move,
      valid.canDelete && remove,
      valid.canRename && rename,
      valid.canManageAccess && manageAccess,
      valid.canTranscribe && transcribe,
      valid.canViewTranscript && viewTranscript,
      // valid.canImportTranscript && convertToTranscript,
    ].filter(Boolean);
  }, [
    // convertToTranscript,
    // nestedMenus,
    valid,
    download,
    manageAccess,
    move,
    // ownership,
    remove,
    rename,
    transcribe,
    viewFile,
    viewTranscript,
  ]);

  return (
    <>
      <PopperMenu ref={ref}>
        {items.map((item, i) => (
          <PopperMenuItem
            key={i}
            onClick={handleClick(item.onClick)}>
            {item.children}
          </PopperMenuItem>
        ))}
      </PopperMenu>
    </>
  );
});

export const FileActionsContextMenuPopper = ({
  placement = 'bottom-end',
  renderAnchor,
}: FileActionsPopperProps) => {

  const showMenu = useShowFileMenu();

  const defaultAnchor = useCallback((state: PopupStateProps) => {
    return (
      <MoreHorizontalAnchorSmall open={state.isOpen} />
    );
  }, []);

  if (!showMenu) {
    return null;
  }

  return (
    <>
      <PopupState variant="popper" popupId="folder-actions-popper">
        {popupState => (
          <div>
            <div {...bindToggle(popupState)}>
              {(renderAnchor || defaultAnchor)(popupState)}
            </div>
            <Popper
              {...bindPopper(popupState)}
              placement={placement}>
              <ClickAwayListener
                onClickAway={() => popupState.close()}>
                <FileActionsContextMenu
                  onClose={popupState.close} />
              </ClickAwayListener>
            </Popper>
          </div>
        )}
      </PopupState>
    </>
  );
};